import type { Notification } from '@repo/types/types/notification'
import { Badge } from '@repo/ui/components/ui/badge'
import { Button } from '@repo/ui/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@repo/ui/components/ui/dropdown-menu'
import { ScrollArea } from '@repo/ui/components/ui/scroll-area'
import { SidebarMenuButton } from '@repo/ui/components/ui/sidebar'
import { Link } from '@tanstack/react-router'
import { Bell, CheckCheck } from 'lucide-react'
import { NotificationMinimal } from '@/components/ui/notifications'
import { useNotifications } from '@/lib/notifications'
import { cn } from '@/lib/utils'

interface NotificationsDropdownProps {
  variant?: 'icon' | 'sidebar'
}

export function NotificationsDropdown({ variant = 'icon' }: NotificationsDropdownProps) {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications()

  const recentNotifications: Notification[] = notifications.slice(0, 5)
  const unreadSummary =
    unreadCount > 0
      ? `${unreadCount} unread`
      : notifications.length > 0
        ? 'All caught up'
        : 'No alerts yet'
  const trigger =
    variant === 'sidebar' ? (
      <SidebarMenuButton
        tooltip="Notifications"
        aria-label="Notifications"
        className="relative cursor-pointer rounded-lg border border-transparent hover:border-sidebar-border/70"
      >
        <Bell className="h-4 w-4" />
        <span>Notifications</span>
        {unreadCount > 0 ? (
          <>
            <span className="ml-auto rounded-full bg-primary/12 px-2 py-0.5 text-[11px] font-semibold text-primary group-data-[collapsible=icon]:hidden">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
            <span className="absolute right-2 top-2 hidden size-2 rounded-full bg-primary group-data-[collapsible=icon]:block" />
          </>
        ) : null}
      </SidebarMenuButton>
    ) : (
      <Button variant="ghost" size="icon" className="relative cursor-pointer">
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <Badge
            variant="destructive"
            className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center p-0 text-xs"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </Badge>
        )}
        <span className="sr-only">Notifications</span>
      </Button>
    )

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        side={variant === 'sidebar' ? 'right' : 'bottom'}
        className="w-80 rounded-[4px]"
      >
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notifications</span>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={markAllAsRead}
              className="h-auto cursor-pointer p-1 text-xs"
            >
              <CheckCheck className="mr-1 h-3 w-3" />
              Mark all read
            </Button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {recentNotifications.length === 0 ? (
          <div className="p-4 text-center text-sm text-muted-foreground">
            {variant === 'sidebar' ? unreadSummary : 'No notifications yet'}
          </div>
        ) : (
          <ScrollArea className="h-[300px]">
            {recentNotifications.map((notification) => (
              <DropdownMenuItem
                key={notification.id}
                className={cn('cursor-pointer p-0', !notification.read && 'bg-muted/50')}
                onClick={() => !notification.read && markAsRead(notification.id)}
              >
                <NotificationMinimal
                  notification={notification}
                  onMarkAsRead={markAsRead}
                  showMarkAsRead={false}
                />
              </DropdownMenuItem>
            ))}
          </ScrollArea>
        )}
        {notifications.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link
                to="/notifications"
                className="w-full cursor-pointer justify-center text-center"
              >
                View all notifications
              </Link>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
